import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronDown, ChevronRight, X } from "lucide-react";

import { Input, Label } from "@/components/ui/Input";
import { useTouchListPickSession } from "@/lib/useTouchListPickSession";
import { cn } from "@/lib/utils";
import { registerViewportLayoutListeners } from "@/lib/useViewportLayoutEvents";
import {
  computeAnchoredListPanel,
  FLOATING_POPOVER_Z_INDEX,
  type AnchoredListPanelBox,
} from "@/lib/viewportAnchoredPopover";

export interface FailureModeTreeOption {
  id: number;
  category: string;
  name: string;
  operatorScripts?: { id: number; name: string; steps: string }[];
}

const LIST_MAX_HEIGHT_PX = 320;
const PANEL_GAP_PX = 4;
const VIEWPORT_EDGE_PX = 8;
const PANEL_SEARCH_CHROME_PX = 48;

function getScrollParents(node: HTMLElement | null): HTMLElement[] {
  const parents: HTMLElement[] = [];
  let element = node?.parentElement;
  while (element) {
    const style = window.getComputedStyle(element);
    const overflowY = style.overflowY;
    const overflowX = style.overflowX;
    const overflow = style.overflow;
    if (
      /(auto|scroll|overlay)/.test(overflowY) ||
      /(auto|scroll|overlay)/.test(overflowX) ||
      /(auto|scroll|overlay)/.test(overflow)
    ) {
      parents.push(element);
    }
    element = element.parentElement;
  }
  return parents;
}

function modeLabel(mode: FailureModeTreeOption): string {
  return `${mode.category} > ${mode.name}`;
}

export function FailureModeTreeSelect({
  id,
  options,
  value,
  onChange,
  loading,
}: {
  id: string;
  options: FailureModeTreeOption[];
  value: FailureModeTreeOption[];
  onChange: (modes: FailureModeTreeOption[]) => void;
  loading: boolean;
}): JSX.Element {
  const generatedId = useId();
  const listboxId = `${id || generatedId}-listbox`;
  const containerRef = useRef<HTMLDivElement | null>(null);
  const fieldRef = useRef<HTMLDivElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const suppressCheckboxChangeRef = useRef(false);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set());
  const [forcedClosed, setForcedClosed] = useState<ReadonlySet<string>>(new Set());
  const suppressCategoryClickRef = useRef(false);
  const [panelBox, setPanelBox] = useState<AnchoredListPanelBox | null>(null);
  const touchListPick = useTouchListPickSession(open);

  const selectedIds = useMemo(() => new Set(value.map((mode) => mode.id)), [value]);

  const groups = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const byCategory = new Map<string, { modes: FailureModeTreeOption[]; childHit: boolean; categoryHit: boolean }>();
    for (const option of options) {
      const categoryHit = needle !== "" && option.category.toLowerCase().includes(needle);
      const nameHit = needle !== "" && option.name.toLowerCase().includes(needle);
      if (needle !== "" && !categoryHit && !nameHit) {
        continue;
      }
      const group = byCategory.get(option.category) ?? {
        modes: [],
        childHit: false,
        categoryHit: false,
      };
      if (needle === "" || categoryHit || nameHit) {
        if (needle === "" || categoryHit || nameHit) {
          group.modes.push(option);
        }
      }
      group.childHit = group.childHit || nameHit;
      group.categoryHit = group.categoryHit || categoryHit || needle === "";
      byCategory.set(option.category, group);
    }
    return [...byCategory.entries()].map(([category, group]) => ({ category, ...group }));
  }, [options, query]);

  useEffect(() => {
    if (!open) {
      setQuery("");
      setExpanded(new Set());
      setForcedClosed(new Set());
    }
  }, [open]);

  useEffect(() => {
    setExpanded(new Set());
    setForcedClosed(new Set());
  }, [query]);

  const updatePanelPosition = (): void => {
    const anchor = fieldRef.current;
    if (anchor == null) return;
    setPanelBox(
      computeAnchoredListPanel({
        anchorRect: anchor.getBoundingClientRect(),
        gap: PANEL_GAP_PX,
        edge: VIEWPORT_EDGE_PX,
        minWidth: anchor.getBoundingClientRect().width,
        chromeHeight: PANEL_SEARCH_CHROME_PX,
        listMaxHeightCap: LIST_MAX_HEIGHT_PX,
      }),
    );
  };

  useLayoutEffect(() => {
    if (!open) {
      setPanelBox(null);
      return;
    }
    updatePanelPosition();
    const onWin = (): void => {
      updatePanelPosition();
    };
    const removeViewport = registerViewportLayoutListeners(onWin);
    const scrollParents = getScrollParents(fieldRef.current);
    for (const parent of scrollParents) {
      parent.addEventListener("scroll", onWin, { passive: true });
    }
    return () => {
      removeViewport();
      for (const parent of scrollParents) {
        parent.removeEventListener("scroll", onWin);
      }
    };
  }, [open, query, options.length]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent): void => {
      const target = event.target as Node;
      if (containerRef.current?.contains(target) || panelRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const frame = requestAnimationFrame(() => {
      searchInputRef.current?.focus();
    });
    return () => cancelAnimationFrame(frame);
  }, [open]);

  function categoryIsOpen(category: string, childHit: boolean, categoryHit: boolean): boolean {
    if (forcedClosed.has(category)) return false;
    if (expanded.has(category)) return true;
    const needle = query.trim();
    return needle !== "" && childHit && !categoryHit;
  }

  function toggleCategory(category: string, currentlyOpen: boolean): void {
    if (currentlyOpen) {
      setExpanded((current) => {
        const next = new Set(current);
        next.delete(category);
        return next;
      });
      setForcedClosed((current) => {
        const next = new Set(current);
        next.add(category);
        return next;
      });
      return;
    }
    setForcedClosed((current) => {
      const next = new Set(current);
      next.delete(category);
      return next;
    });
    setExpanded((current) => {
      const next = new Set(current);
      next.add(category);
      return next;
    });
  }

  function toggleMode(mode: FailureModeTreeOption): void {
    if (selectedIds.has(mode.id)) {
      onChange(value.filter((row) => row.id !== mode.id));
      return;
    }
    onChange([...value, mode]);
  }

  const portalTarget = typeof document !== "undefined" ? document.body : null;
  const listPanel =
    open && panelBox != null && portalTarget
      ? createPortal(
          <div
            ref={panelRef}
            id={listboxId}
            role="listbox"
            aria-multiselectable="true"
            data-searchable-select-panel
            style={{
              position: "fixed",
              top: panelBox.top,
              left: panelBox.left,
              width: panelBox.width,
              zIndex: FLOATING_POPOVER_Z_INDEX,
            }}
            className="overflow-hidden rounded-md border border-slate-200 bg-white shadow-lg"
          >
            <div className="border-b border-slate-100 p-2">
              <Input
                ref={searchInputRef}
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search categories"
                aria-label="Search categories"
                className="h-8 text-sm"
                onKeyDown={(event) => {
                  event.stopPropagation();
                  if (event.key === "Escape") {
                    event.preventDefault();
                    setOpen(false);
                  }
                }}
              />
              <div className="mt-2 flex items-center justify-between text-xs">
                <button
                  type="button"
                  className="text-brand-700 hover:underline disabled:cursor-not-allowed disabled:text-slate-400 disabled:no-underline"
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => onChange([])}
                  disabled={value.length === 0}
                >
                  Clear all
                </button>
                {value.length > 0 ? <span className="text-slate-500">{value.length} selected</span> : null}
              </div>
            </div>
            <ul className="overflow-auto py-1 text-sm" style={{ maxHeight: panelBox.listContentMaxHeight }}>
              {loading ? (
                <li className="px-3 py-2 text-slate-500">Loading failure modes…</li>
              ) : groups.length === 0 ? (
                <li className="px-3 py-2 text-slate-500">No matches.</li>
              ) : (
                groups.map((group) => {
                  const groupOpen = categoryIsOpen(group.category, group.childHit, group.categoryHit);
                  return (
                    <li key={group.category}>
                      <button
                        type="button"
                        className="flex w-full items-center gap-2 px-3 py-2 text-left font-medium text-slate-900 hover:bg-slate-100"
                        onMouseDown={(event) => event.preventDefault()}
                        onPointerDown={(event) => {
                          if (event.pointerType === "mouse") return;
                          touchListPick.onListItemPointerDown(event, () => {
                            suppressCategoryClickRef.current = true;
                            toggleCategory(group.category, groupOpen);
                          });
                        }}
                        onClick={() => {
                          if (suppressCategoryClickRef.current) {
                            suppressCategoryClickRef.current = false;
                            return;
                          }
                          toggleCategory(group.category, groupOpen);
                        }}
                      >
                        {groupOpen ? (
                          <ChevronDown className="h-4 w-4 shrink-0 text-slate-500" aria-hidden />
                        ) : (
                          <ChevronRight className="h-4 w-4 shrink-0 text-slate-500" aria-hidden />
                        )}
                        <span className="min-w-0 truncate">{group.category}</span>
                      </button>
                      {groupOpen
                        ? group.modes.map((mode) => {
                            const checked = selectedIds.has(mode.id);
                            return (
                              <label
                                key={mode.id}
                                className="flex cursor-pointer items-start gap-2 py-2 pl-9 pr-3 hover:bg-slate-100"
                                onMouseDown={(event) => event.preventDefault()}
                                onPointerDown={(event) => {
                                  if (event.pointerType === "mouse") return;
                                  touchListPick.onListItemPointerDown(event, () => {
                                    suppressCheckboxChangeRef.current = true;
                                    toggleMode(mode);
                                  });
                                }}
                              >
                                <input
                                  type="checkbox"
                                  className="mt-0.5 h-4 w-4 shrink-0 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
                                  checked={checked}
                                  onChange={() => {
                                    if (suppressCheckboxChangeRef.current) {
                                      suppressCheckboxChangeRef.current = false;
                                      return;
                                    }
                                    toggleMode(mode);
                                  }}
                                  tabIndex={-1}
                                />
                                <span className="min-w-0 text-left text-slate-800">{mode.name}</span>
                              </label>
                            );
                          })
                        : null}
                    </li>
                  );
                })
              )}
            </ul>
          </div>,
          portalTarget,
        )
      : null;

  return (
    <div ref={containerRef}>
      <Label htmlFor={id}>Failure modes</Label>
      <div
        ref={fieldRef}
        className="mt-1 flex min-h-10 w-full flex-wrap items-center gap-1.5 rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm text-slate-900 focus-within:border-brand-500 focus-within:ring-1 focus-within:ring-brand-500"
        onClick={() => setOpen(true)}
      >
        {value.map((mode) => (
          <span
            key={mode.id}
            className="inline-flex max-w-full items-center gap-1 rounded-md bg-slate-100 px-2 py-0.5 text-xs text-slate-800"
            title={modeLabel(mode)}
          >
            <span className="truncate">{modeLabel(mode)}</span>
            <button
              type="button"
              className="rounded p-0.5 text-slate-500 hover:bg-slate-200 hover:text-slate-800"
              aria-label={`Remove ${modeLabel(mode)}`}
              onClick={(event) => {
                event.stopPropagation();
                onChange(value.filter((row) => row.id !== mode.id));
              }}
            >
              <X className="h-3 w-3" aria-hidden />
            </button>
          </span>
        ))}
        <button
          id={id}
          type="button"
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls={listboxId}
          onClick={(event) => {
            event.stopPropagation();
            setOpen((current) => !current);
          }}
          className={cn(
            "inline-flex min-w-[8rem] flex-1 items-center justify-between gap-2 border-0 bg-transparent px-1 py-1 text-left text-sm focus:outline-none",
            value.length === 0 ? "text-slate-400" : "text-slate-500",
          )}
        >
          <span>{value.length === 0 ? "Select failure modes" : "Add"}</span>
          <ChevronDown className="h-4 w-4 shrink-0 text-slate-500" aria-hidden />
        </button>
      </div>
      {listPanel}
    </div>
  );
}
